import { clamp01, easeInOut, lerp, snapToPixel } from './math.js';

export interface Point {
  x: number;
  y: number;
}

export function orthogonalStep(startX: number, startY: number, targetX: number, targetY: number, amount: number): Point {
  const progress = clamp01(amount);
  const deltaX = targetX - startX;
  const deltaY = targetY - startY;
  const xFirst = Math.abs(deltaX) >= Math.abs(deltaY);
  const turnPoint = 0.52;

  if (progress <= 0) return { x: startX, y: startY };
  if (progress >= 1) return { x: snapToPixel(targetX), y: snapToPixel(targetY) };

  if (xFirst) {
    if (progress < turnPoint) {
      return { x: snapToPixel(lerp(startX, targetX, progress / turnPoint)), y: snapToPixel(startY) };
    }
    return { x: snapToPixel(targetX), y: snapToPixel(lerp(startY, targetY, (progress - turnPoint) / (1 - turnPoint))) };
  }

  if (progress < turnPoint) {
    return { x: snapToPixel(startX), y: snapToPixel(lerp(startY, targetY, progress / turnPoint)) };
  }
  return { x: snapToPixel(lerp(startX, targetX, (progress - turnPoint) / (1 - turnPoint))), y: snapToPixel(targetY) };
}

export function naturalStep(startX: number, startY: number, targetX: number, targetY: number, amount: number): Point {
  const progress = easeInOut(clamp01(amount));
  return { x: snapToPixel(lerp(startX, targetX, progress)), y: snapToPixel(lerp(startY, targetY, progress)) };
}

export function stepPosition(style: string, startX: number, startY: number, targetX: number, targetY: number, amount: number): Point {
  return style === 'natural'
    ? naturalStep(startX, startY, targetX, targetY, amount)
    : orthogonalStep(startX, startY, targetX, targetY, amount);
}

export function catmullRomPointAt(points: Point[], amount: number): Point {
  if (!points.length) return { x: 0, y: 0 };
  if (points.length === 1) return { x: snapToPixel(points[0].x), y: snapToPixel(points[0].y) };

  const progress = clamp01(amount);
  const segmentCount = points.length - 1;
  const scaled = progress * segmentCount;
  const segmentIndex = Math.min(segmentCount - 1, Math.floor(scaled));
  const localT = scaled - segmentIndex;
  const extended = [points[0], ...points, points[points.length - 1]];
  const p0 = extended[segmentIndex];
  const p1 = extended[segmentIndex + 1];
  const p2 = extended[segmentIndex + 2];
  const p3 = extended[segmentIndex + 3];
  const t2 = localT * localT;
  const t3 = t2 * localT;
  const interpolate = (a: number, b: number, c: number, d: number) => 0.5 * (
    (2 * b) + (-a + c) * localT + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3
  );

  return { x: snapToPixel(interpolate(p0.x, p1.x, p2.x, p3.x)), y: snapToPixel(interpolate(p0.y, p1.y, p2.y, p3.y)) };
}

export function catmullRomPoint(points: Point[], amount: number, timing: string = 'eased'): Point {
  const progress = clamp01(amount);
  if (timing !== 'constant') return catmullRomPointAt(points, easeInOut(progress));

  const sampleCount = 24;
  const samples: { t: number; point: Point; totalDistance: number }[] = [];
  let totalDistance = 0;
  let previousPoint = catmullRomPointAt(points, 0);
  samples.push({ t: 0, point: previousPoint, totalDistance: 0 });

  for (let index = 1; index <= sampleCount; index += 1) {
    const t = index / sampleCount;
    const point = catmullRomPointAt(points, t);
    totalDistance += Math.hypot(point.x - previousPoint.x, point.y - previousPoint.y);
    samples.push({ t, point, totalDistance });
    previousPoint = point;
  }

  if (totalDistance <= 0) return previousPoint;

  const targetDistance = totalDistance * progress;
  for (let index = 1; index < samples.length; index += 1) {
    const prev = samples[index - 1];
    const cur = samples[index];
    if (targetDistance <= cur.totalDistance) {
      const segDist = cur.totalDistance - prev.totalDistance;
      const ratio = segDist <= 0 ? 0 : (targetDistance - prev.totalDistance) / segDist;
      return { x: snapToPixel(lerp(prev.point.x, cur.point.x, ratio)), y: snapToPixel(lerp(prev.point.y, cur.point.y, ratio)) };
    }
  }
  return samples[samples.length - 1].point;
}

export function movementDistanceForStyle(style: string, startX: number, startY: number, targetX: number, targetY: number): number {
  const deltaX = Math.abs(targetX - startX);
  const deltaY = Math.abs(targetY - startY);
  return style === 'orthogonal' ? deltaX + deltaY : Math.hypot(deltaX, deltaY);
}

export function pathDistance(points: Point[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += Math.hypot(points[index].x - points[index - 1].x, points[index - 1].y - points[index].y);
  }
  return total;
}

export function pointDistance(a: Point | null, b: Point | null): number {
  return Math.hypot((a?.x || 0) - (b?.x || 0), (a?.y || 0) - (b?.y || 0));
}

export function spotReached(point: Point, target: Point, threshold: number = 12): boolean {
  return pointDistance(point, target) <= threshold;
}

export function movePointTowards(current: Point, target: Point, maxDistance: number): Point {
  const deltaX = target.x - current.x;
  const deltaY = target.y - current.y;
  const distance = Math.hypot(deltaX, deltaY);
  if (distance <= 0 || distance <= maxDistance) {
    return { x: snapToPixel(target.x), y: snapToPixel(target.y) };
  }
  const ratio = maxDistance / distance;
  return { x: snapToPixel(current.x + deltaX * ratio), y: snapToPixel(current.y + deltaY * ratio) };
}
